import type {
  BulkActionSummary,
  BulkActionSummaryInitializeRequest,
  BulkActionSummaryInitializeResponse,
} from '@kizen/api/bulk-actions';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource'
import type { Drop, WithRequired } from './types';

import {
  bulkActionSummary,
  bulkActionSummaryInitialize,
} from '@kizen/api/bulk-actions';
import { getResource } from './resource';
import { curry1 } from './utils';

type BulkActionSummaryInitializeQuery<
  TData = BulkActionSummaryInitializeResponse,
> = WithRequired<
  QueryObserverOptions<
    BulkActionSummaryInitializeResponse,
    DefaultError,
    TData,
    BulkActionSummaryInitializeResponse,
    [
      Resource<
        Readonly<['bulk-actions', 'summary']>,
        undefined,
        BulkActionSummaryInitializeRequest
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

interface BulkActionSummaryQueryOptions<TData>
  extends Omit<
    BulkActionSummaryInitializeQuery<TData>,
    'queryKey' | 'queryFn'
  > {
  body: BulkActionSummaryInitializeRequest;
}

type BulkActionSummaryQuery<TData = BulkActionSummary> = WithRequired<
  QueryObserverOptions<
    BulkActionSummary,
    DefaultError,
    TData,
    BulkActionSummary,
    [Resource<Readonly<['bulk-actions', 'summary', string]>>]
  >,
  'queryFn' | 'queryKey'
>;

export const bulkActionSummaryQuery = <TData = BulkActionSummary>(
  axios: AxiosInstance,
  summaryId: string,
  options: Omit<BulkActionSummaryQuery<TData>, 'queryKey' | 'queryFn'> = {}
): BulkActionSummaryQuery<TData> => {
  const path = ['bulk-actions', 'summary', summaryId] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return bulkActionSummary(axios, qk.path[2], { signal });
    },
    ...options,
  };
};

export const bulkActionSummaryInitializeQuery = <
  TData = BulkActionSummaryInitializeResponse,
>(
  axios: AxiosInstance,
  options: BulkActionSummaryQueryOptions<TData>
): BulkActionSummaryInitializeQuery<TData> => {
  const { body, ...queryOptions } = options;
  const path = ['bulk-actions', 'summary'] as const;
  const resource = getResource(path, { body });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return bulkActionSummaryInitialize(axios, qk.body!, { signal });
    },
    ...queryOptions,
  };
};

class Queries {
  public bulkActionSummaryQuery: <TData = BulkActionSummary>(
    ...args: Drop<Parameters<typeof bulkActionSummaryQuery<TData>>>
  ) => BulkActionSummaryQuery<TData>;
  public bulkActionSummaryInitializeQuery: <
    TData = BulkActionSummaryInitializeResponse,
  >(
    ...args: Drop<Parameters<typeof bulkActionSummaryInitializeQuery<TData>>>
  ) => BulkActionSummaryInitializeQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.bulkActionSummaryQuery = curry1(bulkActionSummaryQuery)(axios);
    this.bulkActionSummaryInitializeQuery = curry1(
      bulkActionSummaryInitializeQuery
    )(axios);
  }
}

export type BulkActionQueries = Queries;

export const defBulkActionQueries = (
  axios: AxiosInstance
): BulkActionQueries => {
  return new Queries(axios);
};
